/**
 * ERF Print Agent - Frontend Client SDK
 *
 * Bridge between Easy Retail Flow POS / TotalClient and ERF Print Agent desktop app.
 * Supports silent PDF printing, cash drawer kick, and printer discovery over
 * WebSocket (ws://127.0.0.1:9191) and HTTP REST (http://127.0.0.1:9190).
 */

const DEFAULT_WS_URL = "ws://127.0.0.1:9191";
const DEFAULT_HTTP_URL = "http://127.0.0.1:9190";
const RECONNECT_DELAY_MS = 5000;
const HEALTH_CHECK_INTERVAL_MS = 30000;

let wsUrl = DEFAULT_WS_URL;
let httpUrl = DEFAULT_HTTP_URL;
let pairingKey = "";
let ws = null;
let connected = false;
let reconnectTimer = null;
let healthCheckTimer = null;
let requestIdCounter = 0;
const pendingRequests = new Map();
let statusListeners = [];

function nextRequestId() {
  return `req-${++requestIdCounter}-${Date.now()}`;
}

/**
 * Convert a Blob / File to a Base64 string.
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result); // Format: "data:application/pdf;base64,..."
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Connect to the ERF Print Agent via WebSocket.
 * @returns {Promise<boolean>}
 */
export function connect() {
  return new Promise((resolve) => {
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      resolve(connected);
      return;
    }

    try {
      const connUrl = pairingKey ? `${wsUrl}?key=${encodeURIComponent(pairingKey)}` : wsUrl;
      ws = new WebSocket(connUrl);

      ws.onopen = () => {
        connected = true;
        clearReconnectTimer();
        notifyStatusListeners("connected");
        startHealthCheck();
        resolve(true);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleMessage(message);
        } catch (err) {
          console.warn("[PrintAgent] Parse error:", err);
        }
      };

      ws.onclose = () => {
        connected = false;
        notifyStatusListeners("disconnected");
        stopHealthCheck();
        rejectAllPending("Agent disconnected");
        scheduleReconnect();
        resolve(false);
      };

      ws.onerror = () => {
        resolve(false);
      };

      setTimeout(() => {
        if (!connected) resolve(false);
      }, 3000);
    } catch {
      scheduleReconnect();
      resolve(false);
    }
  });
}

export function disconnect() {
  clearReconnectTimer();
  stopHealthCheck();
  if (ws) {
    ws.close();
    ws = null;
  }
  connected = false;
}

function scheduleReconnect() {
  clearReconnectTimer();
  reconnectTimer = setTimeout(() => {
    connect();
  }, RECONNECT_DELAY_MS);
}

function clearReconnectTimer() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function startHealthCheck() {
  stopHealthCheck();
  healthCheckTimer = setInterval(async () => {
    try {
      const res = await fetch(`${httpUrl}/health`, { signal: AbortSignal.timeout(3000) });
      if (!res.ok) throw new Error("Health check failed");
    } catch {
      if (connected) {
        disconnect();
        connect();
      }
    }
  }, HEALTH_CHECK_INTERVAL_MS);
}

function stopHealthCheck() {
  if (healthCheckTimer) {
    clearInterval(healthCheckTimer);
    healthCheckTimer = null;
  }
}

function handleMessage(message) {
  if (message.id && pendingRequests.has(message.id)) {
    const { resolve, reject, timeout } = pendingRequests.get(message.id);
    clearTimeout(timeout);
    pendingRequests.delete(message.id);

    if (message.success) {
      resolve(message.data);
    } else {
      reject(new Error(message.error || "Command failed"));
    }
    return;
  }

  if (message.type === "status") {
    notifyStatusListeners("status", message.data);
  }
}

function rejectAllPending(reason) {
  for (const [, { reject, timeout }] of pendingRequests) {
    clearTimeout(timeout);
    reject(new Error(reason));
  }
  pendingRequests.clear();
}

async function sendCommand(command, payload = {}, timeoutMs = 25000) {
  if (!connected) {
    const didConnect = await connect();
    if (!didConnect) {
      throw new Error("ERF Print Agent is not running. Please launch the desktop agent.");
    }
  }

  const id = nextRequestId();

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      pendingRequests.delete(id);
      reject(new Error(`Command '${command}' timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    pendingRequests.set(id, { resolve, reject, timeout });

    try {
      ws.send(JSON.stringify({ id, command, payload }));
    } catch (err) {
      clearTimeout(timeout);
      pendingRequests.delete(id);
      reject(new Error("Failed to send command: " + err.message));
    }
  });
}

function notifyStatusListeners(status, data) {
  for (const listener of statusListeners) {
    try {
      listener(status, data);
    } catch {
      // Ignore listener errors
    }
  }
}

// ─── Public API ─────────────────────────────────────────────────────

export const printAgent = {
  connect,
  disconnect,
  isConnected: () => connected,
  blobToBase64,

  /**
   * Configure the print agent connection.
   */
  configure(config = {}) {
    if (config.pairingKey !== undefined) {
      pairingKey = config.pairingKey || "";
    }
    if (config.wsPort) {
      wsUrl = `ws://127.0.0.1:${config.wsPort}`;
    }
    if (config.httpPort) {
      httpUrl = `http://127.0.0.1:${config.httpPort}`;
    }
    if (config.httpUrl) {
      httpUrl = config.httpUrl;
    }
    if (config.wsUrl) {
      wsUrl = config.wsUrl;
    }
    if (connected) {
      disconnect();
      connect();
    }
  },

  getHttpUrl: () => httpUrl,
  getWsUrl: () => wsUrl,
  getPairingKey: () => pairingKey,

  onStatusChange(callback) {
    statusListeners.push(callback);
    return () => {
      statusListeners = statusListeners.filter((l) => l !== callback);
    };
  },

  /**
   * Check if desktop agent is running.
   */
  async isAgentRunning(customUrl) {
    const targetUrl = customUrl || httpUrl;
    try {
      const res = await fetch(`${targetUrl}/health`, { signal: AbortSignal.timeout(2500) });
      return res.ok;
    } catch {
      return false;
    }
  },

  /**
   * Verify pairing key.
   */
  async verifyKey(key, customUrl) {
    const testKey = key || pairingKey;
    const targetUrl = customUrl || httpUrl;
    if (!testKey) {
      return { valid: false, error: "No pairing key provided" };
    }
    try {
      const res = await fetch(`${targetUrl}/verify-key`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Pairing-Key": testKey,
        },
        signal: AbortSignal.timeout(3000),
      });
      if (res.ok) {
        return { valid: true };
      }
      const data = await res.json().catch(() => ({}));
      return { valid: false, error: data.error || "Invalid key" };
    } catch {
      return { valid: false, error: "Agent is not running" };
    }
  },

  /**
   * Discover available printers using HTTP first (with optional pairing key),
   * falling back to WebSocket.
   *
   * Normalizes the response to an array of standard printer objects:
   * [{ name, displayName, portName, description, isDefault, isNetwork, statusText, ... }]
   */
  async getPrinters(options = {}) {
    const targetUrl = options.httpUrl || httpUrl;
    const key = options.pairingKey || pairingKey;

    // 1. Try HTTP GET /printers
    try {
      const headers = {};
      if (key) {
        headers["X-Pairing-Key"] = key;
      }
      const queryParam = key ? `?key=${encodeURIComponent(key)}` : "";
      const res = await fetch(`${targetUrl}/printers${queryParam}`, {
        headers,
        signal: AbortSignal.timeout(6000),
      });

      if (res.ok) {
        const json = await res.json();
        const rawList =
          json.available ||
          (json.system ? [...json.system, ...(json.usb || [])] : []) ||
          (Array.isArray(json) ? json : []);

        return {
          success: true,
          printers: rawList,
          assignments: json.assignments || null,
          configured: json.configured || null,
          source: "http",
        };
      }
    } catch {
      // HTTP failed, will try WebSocket below
    }

    // 2. Fallback to WebSocket
    try {
      const wsResult = await sendCommand("printers:list", {}, 6000);
      const rawList =
        wsResult?.available ||
        (wsResult?.system ? [...wsResult.system, ...(wsResult.usb || [])] : []) ||
        (Array.isArray(wsResult) ? wsResult : []);

      return {
        success: true,
        printers: rawList,
        assignments: wsResult?.assignments || null,
        configured: wsResult?.configured || null,
        source: "websocket",
      };
    } catch {
      throw new Error("Unable to reach ERF Print Agent. Please verify the agent is running.");
    }
  },

  /**
   * Print PDF stream / blob / base64 / URL.
   */
  async printPdf(pdfData, options = {}) {
    let pdfBase64 = null;
    let pdfUrl = null;

    if (pdfData instanceof Blob) {
      pdfBase64 = await blobToBase64(pdfData);
    } else if (typeof pdfData === "string") {
      if (pdfData.startsWith("http://") || pdfData.startsWith("https://") || pdfData.startsWith("blob:")) {
        pdfUrl = pdfData;
      } else {
        pdfBase64 = pdfData;
      }
    } else {
      throw new Error("Invalid PDF data. Provide a Blob, Base64 string, or URL.");
    }

    return sendCommand("print:pdf", {
      pdfBase64,
      pdfUrl,
      printerType: options.printerType || "receipt",
      printerName: options.printerName,
      copies: options.copies || 1,
      openDrawer: options.openDrawer || false,
    });
  },

  /**
   * Print receipt directly.
   */
  async printReceipt(pdfData, options = {}) {
    return this.printPdf(pdfData, {
      ...options,
      printerType: "receipt",
    });
  },

  /**
   * Print label directly.
   */
  async printLabel(pdfData, options = {}) {
    return this.printPdf(pdfData, {
      ...options,
      printerType: "label",
    });
  },

  /**
   * Print invoice directly.
   */
  async printInvoice(pdfData, options = {}) {
    return this.printPdf(pdfData, {
      ...options,
      printerType: "invoice",
    });
  },

  /**
   * Kick open cash drawer.
   */
  async openCashDrawer(options = {}) {
    return sendCommand("drawer:open", options);
  },

  /**
   * Ping agent status.
   */
  async getStatus() {
    return sendCommand("status:ping");
  },
};

export default printAgent;
